import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SolutionsPageTemplate from "../../components/SolutionsPageTemplate/SolutionsPageTemplate";

const SEO_TITLE = "Remote Team Productivity and Collaboration Software | TeamGrid";
const SEO_DESCRIPTION = "Build high performing remote teams with timezone alignment, collaboration insights and burnout prevention without micromanagement.";
const SEO_CANONICAL = "https://teamgrid.ai/solutions/remote-team-productivity";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const RemoteDistributedTeams = () => {
  const navigate = useNavigate();
  // FAQ data specific to Remote & Distributed Teams
  const remoteTeamsFAQs = [
    {
      question: "How does TeamGrid help remote teams stay aligned across time zones?",
      answer:
        "TeamGrid visualizes collaboration, overlap hours, and async workflows to support remote team productivity across time zones.",
    },
    {
      question: "Does TeamGrid track remote workers' screens?",
      answer:
        "No, TeamGrid respects privacy and focuses on productivity signals, not screenshots or employee surveillance.",
    },
    {
      question: "Can TeamGrid help reduce unnecessary meetings?",
      answer:
        "Yes, by identifying focus time vs. meeting time, TeamGrid helps leaders cut unproductive meetings and improve remote productivity.",
    },
  ];

  // Productivity cards data
  const productivityCards = [
    {
      title: "Timezone Alignment",
      description:
        '"Distributed teams work across timezones, making decision-making timing difficult."',
      points: [
        "Visualizes real working-hour overlaps",
        "Identifies optimal sync windows",
        "Promotes fair meeting schedules",
      ],
    },
    {
      title: "Connection & Engagement",
      description:
        '"Disengagement and isolation can develop quietly."',
      points: [
        "Highlights collaboration frequency and interaction patterns",
        "Identifies silos early across teams",
        "Tracks manager-team connection health",
      ],
    },
    {
      title: "Async Readiness",
      description:
        '"Async work depends on clear documentation and strong handoffs."',
      points: [
        "Measures response times and velocity",
        "Evaluates documentation effectiveness",
        "Identifies workflow bottlenecks",
      ],
    },
    {
      title: "Focus Time Balance",
      description:
        '"Remote productivity depends on protecting deep work while enabling collaboration."',
      points: [
        "Tracks meeting vs deep work ratio",
        "Identifies interruption patterns",
        "Ensures equitable participation",
      ],
    },
  ];

  const customButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate("/book-demo")}
      className="flex items-center justify-center px-7 py-3.5 rounded-[14px] font-semibold text-black transition-all"
      style={{
        background: "#00D1C1",
        boxShadow: "0px 18px 40px rgba(0, 209, 193, 0.45)",
      }}
    >
      Get a Demo
    </motion.button>
  );

  return (
    <>
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <link rel="canonical" href={SEO_CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SEO_CANONICAL} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={SEO_CANONICAL} />
        <meta name="twitter:title" content={SEO_TITLE} />
        <meta name="twitter:description" content={SEO_DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "How does TeamGrid help remote teams stay aligned across time zones?", acceptedAnswer: { "@type": "Answer", text: "TeamGrid visualizes collaboration patterns, overlap hours, and asynchronous workflows to support alignment across time zones." } },
            { "@type": "Question", name: "Does TeamGrid track remote workers' screens?", acceptedAnswer: { "@type": "Answer", text: "No, TeamGrid does not track screens or perform employee surveillance. It focuses only on high-level productivity signals." } },
            { "@type": "Question", name: "Can TeamGrid help reduce unnecessary meetings?", acceptedAnswer: { "@type": "Answer", text: "Yes, by identifying focus time versus meeting time, TeamGrid helps leaders reduce unproductive meetings and improve remote team efficiency." } },
          ],
        })}</script>
      </Helmet>
      <SolutionsPageTemplate
        breadcrumbTitle="Remote Teams"
      heroTitle="Remote Team Productivity & Collaboration Insights"
      heroSubtitle="Build high-performing remote teams across timezones. Ensure alignment, prevent burnout, and measure outcomes without micromanagement."
      heroTextCenter={false}
      primaryButtonText="Get a Demo"
      productivityTitle="Key Productivity Areas"
      productivitySubtitle="See how high-performing remote teams track work without friction."
      productivityCardIcons={[
        "/Background_sol_pg_1.webp",
        "/Background.webp",
        "/Background_sol_pg_2.webp",
        "/Background_sol_pg_3.webp",
      ]}
      productivityCardAccentColors={[
        "#00D1C1", // teal (globe)
        "#3B82F6", // blue (connections)
        "#8B5CF6", // purple (network)
        "#F97316", // orange (target)
      ]}
      featureSuiteTitle="Complete Feature Suite"
      featureSuiteSubtitle="Everything you need for remote team management."
      outcomesTitle="Outcomes You Can Expect"
      outcomesSubtitle=""
      whyChooseTitle="Why Remote Teams Choose TeamGrid?"
      whyChooseDescription="TeamGrid is more than a remote team time tracking software. It’s a productivity intelligence platform for remote and distributed teams, built to improve delivery and engagement."
      customPrimaryButton={customButton}
      productivityCards={productivityCards}
        faqs={remoteTeamsFAQs}
        faqSubtitle="Everything you need to know about TeamGrid for remote and distributed teams."
      />
    </>
  );
};

export default RemoteDistributedTeams;
